import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { setActiveSeasonId } from "./api/axiosClient";
import { seasonService } from "./api/service.ts";
import type { Season, Tournament } from "./api/types";

const STORAGE_KEY = "selectedSeasonId";

interface SeasonContextType {
  seasons: Season[];
  selectedSeason: Season | null;
  currentSeason: Season | null;
  /** true se la stagione selezionata non è quella in corso (sola consultazione) */
  isArchived: boolean;
  /** Torneo (non amichevoli) della stagione selezionata, null se non è ancora stato creato */
  tournamentId: number | null;
  tournaments: Tournament[];
  selectSeason: (seasonId: number) => void;
}

const SeasonContext = createContext<SeasonContextType>({
  seasons: [],
  selectedSeason: null,
  currentSeason: null,
  isArchived: false,
  tournamentId: null,
  tournaments: [],
  selectSeason: () => {},
});

function readStoredSeasonId(): number | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value ? Number(value) : null;
  } catch {
    return null;
  }
}

function storeSeasonId(seasonId: number | null) {
  try {
    if (seasonId == null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, String(seasonId));
  } catch {
    // localStorage non disponibile: la scelta vale solo per la sessione
  }
}

/**
 * Carica le stagioni e mantiene quella selezionata (default: stagione corrente).
 * La stagione viene inviata al BE come header X-Season-Id; al cambio di stagione
 * i figli vengono rimontati così ogni componente ricarica i propri dati.
 */
export function SeasonProvider({ children }: { children: React.ReactNode }) {
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    seasonService
      .getSeasons()
      .then((list) => {
        setSeasons(list);
        const stored = readStoredSeasonId();
        const initial =
          list.find((s) => s.id === stored) ?? list.find((s) => s.current) ?? list[0] ?? null;
        if (initial) {
          setActiveSeasonId(initial.id);
          setSelectedId(initial.id);
        }
      })
      .catch((err) => console.error("Errore caricamento stagioni", err))
      .finally(() => setReady(true));

    return () => setActiveSeasonId(null);
  }, []);

  useEffect(() => {
    if (selectedId == null) return;
    seasonService
      .getTournaments()
      .then(setTournaments)
      .catch(() => setTournaments([]));
  }, [selectedId]);

  const selectSeason = useCallback((seasonId: number) => {
    // l'header va aggiornato prima del rimontaggio dei figli, che ricaricano subito i dati
    setActiveSeasonId(seasonId);
    storeSeasonId(seasonId);
    setTournaments([]);
    setSelectedId(seasonId);
  }, []);

  const value = useMemo<SeasonContextType>(() => {
    const selectedSeason = seasons.find((s) => s.id === selectedId) ?? null;
    const currentSeason = seasons.find((s) => s.current) ?? null;
    return {
      seasons,
      selectedSeason,
      currentSeason,
      isArchived: !!selectedSeason && !selectedSeason.current,
      // il torneo vero, non il contenitore delle amichevoli (creato magari per primo)
      tournamentId: (tournaments.find((t) => t.competitionType !== "FRIENDLY") ?? null)?.id ?? null,
      tournaments,
      selectSeason,
    };
  }, [seasons, selectedId, tournaments, selectSeason]);

  if (!ready) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <SeasonContext.Provider value={value}>
      <React.Fragment key={selectedId ?? "none"}>{children}</React.Fragment>
    </SeasonContext.Provider>
  );
}

export function useSeason() {
  return useContext(SeasonContext);
}
